import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Upload, FileMusic } from 'lucide-react';
import MidiFileUpload from './components/MidiFileUpload';
import TrackSelector from './components/TrackSelector';
import TrackPreview from './components/TrackPreview';

/**
 * Admin MIDI Upload Tool
 * For admins to upload and manage melody MIDI files
 */
export default function AdminMidiUploadTool() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState('upload'); // upload, select, preview
  const [uploadedMidi, setUploadedMidi] = useState(null);
  const [selectedTrack, setSelectedTrack] = useState(null);

  const handleMidiUpload = (midiData) => {
    setUploadedMidi(midiData);
    setCurrentStep('select');
  };

  const handleTrackSelect = (track) => {
    setSelectedTrack(track);
    setCurrentStep('preview');
  };

  const handleSave = () => {
    setCurrentStep('upload');
    setUploadedMidi(null);
    setSelectedTrack(null);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-gradient-to-br from-blue-500 to-purple-500 rounded-xl">
                <FileMusic className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  MIDI Upload Tool
                </h1>
                <p className="text-gray-600">Manage melody tracks for Pitch Coach</p>
              </div>
            </div>

            <button
              onClick={() => navigate('/tools-admin-page')}
              className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-all"
            >
              Back to Tools
            </button>
          </div>
        </div>

        {/* Progress Steps */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <div className="flex items-center justify-between">
            <div className={`flex items-center gap-3 ${currentStep === 'upload' ? 'text-purple-600' : 'text-gray-400'}`}>
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${currentStep === 'upload' ? 'bg-purple-600 text-white' : 'bg-gray-200'}`}>
                1
              </div>
              <span className="font-medium">Upload MIDI</span>
            </div>

            <div className="flex-1 h-1 mx-4 bg-gray-200">
              <div className={`h-full transition-all ${currentStep !== 'upload' ? 'bg-purple-600' : 'bg-gray-200'}`} style={{ width: currentStep === 'preview' ? '100%' : currentStep === 'select' ? '50%' : '0%' }} />
            </div>

            <div className={`flex items-center gap-3 ${currentStep === 'select' ? 'text-purple-600' : 'text-gray-400'}`}>
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${currentStep === 'select' ? 'bg-purple-600 text-white' : 'bg-gray-200'}`}>
                2
              </div>
              <span className="font-medium">Select Track</span>
            </div>

            <div className="flex-1 h-1 mx-4 bg-gray-200">
              <div className={`h-full transition-all ${currentStep === 'preview' ? 'bg-purple-600 w-full' : 'bg-gray-200 w-0'}`} />
            </div>

            <div className={`flex items-center gap-3 ${currentStep === 'preview' ? 'text-purple-600' : 'text-gray-400'}`}>
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${currentStep === 'preview' ? 'bg-purple-600 text-white' : 'bg-gray-200'}`}>
                3
              </div>
              <span className="font-medium">Preview & Save</span>
            </div>
          </div>
        </div>

        {/* Main Content */}
        {currentStep === 'upload' && (
          <MidiFileUpload onMidiUpload={handleMidiUpload} />
        )}

        {currentStep === 'select' && uploadedMidi && (
          <TrackSelector 
            midiData={uploadedMidi} 
            onTrackSelect={handleTrackSelect}
            onBack={() => setCurrentStep('upload')}
          />
        )}

        {currentStep === 'preview' && selectedTrack && (
          <TrackPreview 
            midiData={uploadedMidi}
            selectedTrack={selectedTrack}
            onSave={handleSave}
            onBack={() => setCurrentStep('select')}
          />
        )}
      </div>
    </div>
  );
}